import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  onSnapshot,
  getDocs,
  getCountFromServer,
  query,
  orderBy,
  limit,
  startAfter,
  updateDoc,
  where,
} from "firebase/firestore";
import { signInWithPopup, signOut, onAuthStateChanged, User } from "firebase/auth";
import { db, auth, googleProvider, isFirebaseConfigured } from "../firebase";
import { Match, Prayer, ThankMessage } from "../types";
import { syncMatches } from "../utils/lolesports";
import { getThreadsUrl } from "../utils/threads";
import TeamCrest from "../components/TeamCrest";
import { leagueLabel } from "../utils/tournament";

const T1_LOGO = "/t1-logo.png";

const BG_IMAGE = "/G-XvDjdboAAEwI6.jpeg";

// Search never downloads the whole collection (26K+ docs would burn half the
// free daily read quota): text matches come from the most recent
// SEARCH_POOL_SIZE prayers, username-prefix matches from a server query.
const SEARCH_POOL_SIZE = 1000;

const toPrayer = (d: any): Prayer => {
  const raw = d.data();
  if (raw.createdAt && typeof raw.createdAt !== "number") {
    raw.createdAt = raw.createdAt.toMillis ? raw.createdAt.toMillis() : Number(raw.createdAt);
  }
  return { id: d.id, ...raw } as Prayer;
};

const Admin: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState<Match[]>([]);

  const [opponent, setOpponent] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [tournament, setTournament] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState("");
  const [showPast, setShowPast] = useState(false);
  const [thankMessages, setThankMessages] = useState<ThankMessage[]>([]);
  const [newThankMsg, setNewThankMsg] = useState("");
  const [prayers, setPrayers] = useState<Prayer[]>([]);
  const [totalPrayerCount, setTotalPrayerCount] = useState(0);
  const [hasMorePrayers, setHasMorePrayers] = useState(false);
  const [loadingPrayers, setLoadingPrayers] = useState(false);
  const lastPrayerDoc = useRef<any>(null);
  const ADMIN_PAGE_SIZE = 30;
  const [activeTab, setActiveTab] = useState<"matches" | "thanks" | "prayers">("matches");
  const [prayerSearch, setPrayerSearch] = useState("");
  const [searchPool, setSearchPool] = useState<Prayer[] | null>(null);
  const [loadingSearchPool, setLoadingSearchPool] = useState(false);
  const [usernameMatches, setUsernameMatches] = useState<Prayer[]>([]);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(prayerSearch), 300);
    return () => clearTimeout(timer);
  }, [prayerSearch]);

  const searchKeyword = debouncedSearch.trim().toLowerCase();
  const isSearching = !!searchKeyword;

  const filteredPrayers = useMemo(() => {
    if (!isSearching) return prayers;
    const byId = new Map<string, Prayer>();
    for (const p of searchPool || []) {
      if (
        p.threadsUsername.toLowerCase().includes(searchKeyword) ||
        p.prayerContent.toLowerCase().includes(searchKeyword)
      ) {
        byId.set(p.id, p);
      }
    }
    for (const p of usernameMatches) byId.set(p.id, p);
    return Array.from(byId.values()).sort((a, b) => b.createdAt - a.createdAt);
  }, [isSearching, searchKeyword, searchPool, usernameMatches, prayers]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser?.email) {
        const adminDoc = await import("firebase/firestore").then(({ getDoc, doc: docRef }) =>
          getDoc(docRef(db, "admins", firebaseUser.email!))
        );
        setIsAdmin(adminDoc.exists());
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isAdmin || !isFirebaseConfigured) return;

    const q = query(collection(db, "matches"), orderBy("date", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data: Match[] = [];
      snapshot.forEach((d) => {
        data.push({ id: d.id, ...d.data() } as Match);
      });
      setMatches(data);
    });

    return () => unsubscribe();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin || !isFirebaseConfigured) return;

    const unsubscribe = onSnapshot(collection(db, "thankMessages"), (snapshot) => {
      const data: ThankMessage[] = [];
      snapshot.forEach((d) => {
        data.push({ id: d.id, ...d.data() } as ThankMessage);
      });
      data.sort((a, b) => b.createdAt - a.createdAt);
      setThankMessages(data);
    });

    return () => unsubscribe();
  }, [isAdmin]);

  const fetchPrayers = useCallback(async (reset = false) => {
    if (!isFirebaseConfigured) return;
    setLoadingPrayers(true);
    try {
      const baseQuery = reset
        ? query(collection(db, "prayers"), orderBy("createdAt", "desc"), limit(ADMIN_PAGE_SIZE))
        : query(collection(db, "prayers"), orderBy("createdAt", "desc"), startAfter(lastPrayerDoc.current), limit(ADMIN_PAGE_SIZE));

      const [snapshot, countResult] = await Promise.all([
        getDocs(baseQuery),
        reset ? getCountFromServer(query(collection(db, "prayers"))) : Promise.resolve(null),
      ]);

      const data: Prayer[] = [];
      snapshot.forEach((d) => {
        const raw = d.data();
        if (raw.createdAt && typeof raw.createdAt !== "number") {
          raw.createdAt = raw.createdAt.toMillis ? raw.createdAt.toMillis() : Number(raw.createdAt);
        }
        data.push({ id: d.id, ...raw } as Prayer);
      });
      lastPrayerDoc.current = snapshot.docs[snapshot.docs.length - 1] || null;
      setHasMorePrayers(data.length === ADMIN_PAGE_SIZE);

      if (reset) {
        setPrayers(data);
        if (countResult) setTotalPrayerCount(countResult.data().count);
      } else {
        setPrayers((prev) => [...prev, ...data]);
      }
    } catch (error) {
      console.error("Error fetching prayers:", error);
    } finally {
      setLoadingPrayers(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    fetchPrayers(true);
  }, [isAdmin, fetchPrayers]);

  // Loaded once per page visit, on the first search.
  const fetchSearchPool = useCallback(async () => {
    if (searchPool || !isFirebaseConfigured) return;
    setLoadingSearchPool(true);
    try {
      const snapshot = await getDocs(
        query(collection(db, "prayers"), orderBy("createdAt", "desc"), limit(SEARCH_POOL_SIZE))
      );
      setSearchPool(snapshot.docs.map(toPrayer));
    } catch (error) {
      console.error("Error fetching recent prayers for search:", error);
    } finally {
      setLoadingSearchPool(false);
    }
  }, [searchPool]);

  useEffect(() => {
    if (isSearching) fetchSearchPool();
  }, [isSearching, fetchSearchPool]);

  // Username prefix match across ALL prayers (cheap: index range query).
  useEffect(() => {
    const term = debouncedSearch.trim().replace(/^@/, "");
    if (!term || !isFirebaseConfigured) {
      setUsernameMatches([]);
      return;
    }
    let cancelled = false;
    getDocs(
      query(
        collection(db, "prayers"),
        where("threadsUsername", ">=", term),
        where("threadsUsername", "<=", term + ""),
        limit(50)
      )
    )
      .then((snapshot) => {
        if (!cancelled) setUsernameMatches(snapshot.docs.map(toPrayer));
      })
      .catch((error) => console.error("Error searching usernames:", error));
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch]);

  const handleDeletePrayer = async (id: string) => {
    if (window.confirm("Xóa lời khấn này?")) {
      await deleteDoc(doc(db, "prayers", id));
      setPrayers((prev) => prev.filter((p) => p.id !== id));
      setSearchPool((prev) => (prev ? prev.filter((p) => p.id !== id) : null));
      setUsernameMatches((prev) => prev.filter((p) => p.id !== id));
      setTotalPrayerCount((prev) => Math.max(0, prev - 1));
    }
  };

  const handleAddThankMsg = async () => {
    if (!newThankMsg.trim()) return;
    await addDoc(collection(db, "thankMessages"), {
      message: newThankMsg.trim(),
      createdAt: Date.now(),
    });
    setNewThankMsg("");
  };

  const handleDeleteThankMsg = async (id: string) => {
    await deleteDoc(doc(db, "thankMessages", id));
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error: any) {
      if (error.code !== "auth/popup-closed-by-user") {
        console.error("Sign-in error:", error);
        alert(`Lỗi đăng nhập: ${error.code || error.message}`);
      }
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
  };

  const handleAddMatch = async () => {
    if (!opponent || !date || !time) return;

    await addDoc(collection(db, "matches"), {
      opponent: opponent.trim(),
      date,
      time,
      tournament: tournament.trim(),
      isActive: true,
      createdAt: Date.now(),
    });

    setOpponent("");
    setDate("");
    setTime("");
    setTournament("");
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Xóa trận này?")) {
      await deleteDoc(doc(db, "matches", id));
    }
  };

  const handleToggle = async (id: string, active: boolean) => {
    await updateDoc(doc(db, "matches", id), { isActive: !active });
  };

  const handleDeletePast = async (ids: string[]) => {
    if (ids.length === 0) return;
    if (!window.confirm(`Xóa ${ids.length} trận đã qua?`)) return;
    await Promise.all(ids.map((id) => deleteDoc(doc(db, "matches", id))));
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult("");
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("Chưa đăng nhập");
      const s = await syncMatches(token);
      if (s.upcoming === 0 && s.created === 0 && s.updated === 0) {
        setSyncResult("LoL Esports chưa công bố trận T1 sắp tới (đối thủ còn TBD)");
      } else if (s.created + s.updated > 0) {
        setSyncResult(`Đã thêm ${s.created}, cập nhật ${s.updated} trận từ LoL Esports`);
      } else {
        setSyncResult(`Lịch đã khớp với LoL Esports (${s.upcoming} trận sắp tới)`);
      }
    } catch (error: any) {
      setSyncResult(`Lỗi: ${error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const inputClass =
    "p-3.5 bg-black/40 border border-white/10 rounded-lg text-white placeholder-white/25 focus:border-primary/60 focus:bg-black/50 focus:outline-none focus:ring-1 focus:ring-primary/30 transition-all font-body w-full";

  const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="relative min-h-screen">
      <div
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url(${BG_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
        }}
      />
      <div
        className="fixed inset-0 z-0"
        style={{
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.75) 30%, rgba(0,0,0,0.85) 60%, rgba(0,0,0,0.95) 100%)",
        }}
      />
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-8">
        {children}
      </div>
    </div>
  );

  if (loading) {
    return (
      <PageWrapper>
        <div className="text-white/40 font-body">Đang tải...</div>
      </PageWrapper>
    );
  }

  if (!user) {
    return (
      <PageWrapper>
        <div className="glass-card rounded-2xl p-10 max-w-sm w-full text-center">
          <div className="w-12 h-0.5 bg-primary mx-auto mb-6" />
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter leading-none mb-2">
            <span className="text-primary drop-shadow-[0_0_40px_rgba(220,38,38,0.6)]">
              ADMIN
            </span>
          </h1>
          <p className="text-white/40 text-sm mb-8 font-body tracking-wide">
            Quản lý lịch thi đấu T1
          </p>
          <div className="w-12 h-0.5 bg-primary mx-auto mb-8" />
          <button
            onClick={handleGoogleSignIn}
            className="w-full p-3.5 bg-white text-black font-body font-medium rounded-xl transition hover:bg-white/90 flex items-center justify-center gap-3 cursor-pointer"
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Đăng nhập bằng Google
          </button>
          <a
            href="/"
            className="block text-white/30 text-sm mt-5 hover:text-white transition font-body"
          >
            Quay về trang chủ
          </a>
        </div>
      </PageWrapper>
    );
  }

  if (!isAdmin) {
    return (
      <PageWrapper>
        <div className="glass-card rounded-2xl p-10 max-w-sm w-full text-center">
          <div className="w-12 h-0.5 bg-primary mx-auto mb-6" />
          <h1 className="font-heading text-3xl font-bold tracking-tighter leading-none mb-3">
            <span className="text-primary drop-shadow-[0_0_30px_rgba(220,38,38,0.5)]">
              KHÔNG CÓ QUYỀN
            </span>
          </h1>
          <p className="text-white/40 text-sm mb-2 font-body">
            Tài khoản <span className="text-white">{user.email}</span> không phải admin.
          </p>
          <div className="w-12 h-0.5 bg-primary mx-auto my-6" />
          <div className="flex gap-3">
            <button
              onClick={handleSignOut}
              className="flex-1 p-3 bg-white/5 border border-white/10 text-white/60 font-body rounded-xl hover:bg-white/10 hover:text-white transition cursor-pointer"
            >
              Đổi tài khoản
            </button>
            <a
              href="/"
              className="flex-1 p-3 bg-primary text-white font-body rounded-xl hover:bg-primary/80 transition text-center"
            >
              Trang chủ
            </a>
          </div>
        </div>
      </PageWrapper>
    );
  }

  if (!isFirebaseConfigured) {
    return (
      <PageWrapper>
        <div className="text-center">
          <h1 className="font-heading text-3xl font-bold text-primary mb-4">
            Firebase chưa được cấu hình
          </h1>
          <p className="text-white/40 font-body">
            Vui lòng thêm Firebase config vào file .env
          </p>
        </div>
      </PageWrapper>
    );
  }

  return (
    <div className="relative min-h-screen">
      <div
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url(${BG_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
        }}
      />
      <div
        className="fixed inset-0 z-0"
        style={{
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.75) 30%, rgba(0,0,0,0.85) 60%, rgba(0,0,0,0.95) 100%)",
        }}
      />

      <div className="relative z-10 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 mb-8 pt-4">
            <div className="flex flex-col items-start">
              <div className="w-12 h-0.5 bg-primary mb-4" />
              <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter leading-none">
                <span className="text-primary drop-shadow-[0_0_40px_rgba(220,38,38,0.6)]">
                  QUẢN LÝ
                </span>
                <span className="text-white drop-shadow-[0_0_20px_rgba(255,255,255,0.2)]">
                  {" "}LỊCH ĐẤU
                </span>
              </h1>
              <p className="text-white/30 text-sm font-body mt-2 tracking-wide">
                {user.email}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSignOut}
                className="px-5 py-2.5 bg-black/40 border border-white/10 text-white/60 hover:text-white hover:border-white/20 rounded-xl transition text-sm font-body cursor-pointer"
              >
                Đăng xuất
              </button>
              <a
                href="/"
                className="px-5 py-2.5 bg-primary/10 border border-primary/20 text-primary hover:bg-primary/20 rounded-xl transition text-sm font-body"
              >
                Trang chủ
              </a>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-0 mb-8 border-b border-white/10">
            <button
              onClick={() => setActiveTab("matches")}
              className={`px-6 py-3 text-sm font-heading font-bold transition cursor-pointer border-b-2 -mb-px bg-transparent ${
                activeTab === "matches"
                  ? "border-primary text-primary"
                  : "border-transparent text-white/70 hover:text-white"
              }`}
            >
              Lịch thi đấu
            </button>
            <button
              onClick={() => setActiveTab("prayers")}
              className={`px-6 py-3 text-sm font-heading font-bold transition cursor-pointer border-b-2 -mb-px bg-transparent ${
                activeTab === "prayers"
                  ? "border-primary text-primary"
                  : "border-transparent text-white/70 hover:text-white"
              }`}
            >
              Lời khấn ({totalPrayerCount})
            </button>
            <button
              onClick={() => setActiveTab("thanks")}
              className={`px-6 py-3 text-sm font-heading font-bold transition cursor-pointer border-b-2 -mb-px bg-transparent ${
                activeTab === "thanks"
                  ? "border-primary text-primary"
                  : "border-transparent text-white/70 hover:text-white"
              }`}
            >
              Lời cảm ơn ({thankMessages.length})
            </button>
          </div>

          {activeTab === "matches" && (
            <>
              {/* Auto Sync */}
              <div className="glass-card rounded-2xl p-6 md:p-8 mb-6">
                <h2 className="font-heading text-lg font-bold text-white mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                  Đồng bộ từ LoL Esports
                </h2>
                <p className="text-white/40 text-sm font-body mb-4">
                  Tự động lấy lịch thi đấu T1 từ LoL Esports API (LCK). Trận đã có sẽ không bị trùng.
                </p>
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleSync}
                    disabled={syncing}
                    className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-heading font-bold rounded-xl transition cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {syncing ? "Đang đồng bộ..." : "Đồng bộ lịch T1"}
                  </button>
                  {syncResult && (
                    <span className={`text-sm font-body ${syncResult.startsWith("Lỗi") ? "text-red-400" : "text-green-400"}`}>
                      {syncResult}
                    </span>
                  )}
                </div>
              </div>

              {/* Add Match Form */}
              <div className="glass-card rounded-2xl p-6 md:p-8 mb-8">
                <h2 className="font-heading text-lg font-bold text-white mb-5 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                  Thêm trận thủ công
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                  <div>
                    <label className="block text-white/40 text-xs uppercase tracking-wider mb-2 font-heading">
                      Đối thủ
                    </label>
                    <input
                      type="text"
                      placeholder="Gen.G, HLE, DRX..."
                      value={opponent}
                      onChange={(e) => setOpponent(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-white/40 text-xs uppercase tracking-wider mb-2 font-heading">
                      Giải đấu
                    </label>
                    <input
                      type="text"
                      placeholder="LCK Spring 2026"
                      value={tournament}
                      onChange={(e) => setTournament(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-white/40 text-xs uppercase tracking-wider mb-2 font-heading">
                      Ngày
                    </label>
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className={inputClass}
                      style={{ colorScheme: "dark" }}
                    />
                  </div>
                  <div>
                    <label className="block text-white/40 text-xs uppercase tracking-wider mb-2 font-heading">
                      Giờ
                    </label>
                    <input
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      className={inputClass}
                      style={{ colorScheme: "dark" }}
                    />
                  </div>
                </div>
                <button
                  onClick={handleAddMatch}
                  disabled={!opponent || !date || !time}
                  className="px-8 py-3.5 bg-gradient-to-r from-primary to-primary-dark hover:from-primary-light hover:to-primary text-white font-heading font-bold rounded-xl transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed shadow-[0_0_20px_rgba(220,38,38,0.2)] hover:shadow-[0_0_30px_rgba(220,38,38,0.4)] disabled:shadow-none"
                >
                  Thêm trận
                </button>
              </div>

              {/* Match List — upcoming first, past collapsed */}
              {(() => {
                const DURATION = 4 * 60 * 60 * 1000;
                const nowMs = Date.now();
                const ended = (m: Match) =>
                  new Date(`${m.date}T${m.time}:00`).getTime() + DURATION < nowMs;
                const upcoming = matches
                  .filter((m) => !ended(m))
                  .sort((a, b) =>
                    `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`)
                  );
                const past = matches
                  .filter(ended)
                  .sort((a, b) =>
                    `${b.date} ${b.time}`.localeCompare(`${a.date} ${a.time}`)
                  );

                const renderRow = (match: Match, isPast: boolean) => (
                  <div
                    key={match.id}
                    className={`glass-card rounded-xl p-5 flex flex-col md:flex-row justify-between md:items-center gap-4 ${
                      isPast ? "opacity-40" : ""
                    }`}
                  >
                    <div>
                      <div className="font-heading font-bold text-base text-white flex items-center gap-2 flex-wrap">
                        <TeamCrest name="T1" logoUrl={T1_LOGO} size={20} />
                        T1 vs
                        <TeamCrest
                          name={match.opponent}
                          logoUrl={match.opponentLogo}
                          size={20}
                        />
                        {match.opponent}
                        {!isPast && match.isActive && (
                          <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-primary font-semibold">
                            <span className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
                            Mở khấn
                          </span>
                        )}
                        {!isPast && !match.isActive && (
                          <span className="text-white/30 text-xs font-body font-normal">
                            Đã đóng
                          </span>
                        )}
                        {match.stale && (
                          <span
                            className="text-amber-400/80 text-xs font-body font-normal"
                            title="LoL Esports không còn xếp T1 vào trận này nên trận đang bị ẩn khỏi trang chủ"
                          >
                            Riot đã gỡ · đang ẩn
                          </span>
                        )}
                      </div>
                      <div className="text-white/40 text-sm font-body mt-1 flex items-center gap-2 flex-wrap">
                        <span>
                          {new Date(match.date + "T00:00:00").toLocaleDateString(
                            "vi-VN",
                            {
                              weekday: "short",
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            }
                          )}
                        </span>
                        <span className="text-white/15">•</span>
                        <span>{match.time}</span>
                        {leagueLabel(match.tournament) && (
                          <>
                            <span className="text-white/15">•</span>
                            <span className="text-white/25">
                              {leagueLabel(match.tournament)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => handleToggle(match.id, match.isActive)}
                        className={`px-4 py-2 rounded-lg text-xs font-body cursor-pointer transition ${
                          match.isActive
                            ? "bg-white/[0.06] border border-white/10 text-white/60 hover:bg-white/10 hover:text-white"
                            : "bg-primary/10 border border-primary/20 text-primary/80 hover:bg-primary/20 hover:text-primary"
                        }`}
                      >
                        {match.isActive ? "Đóng khấn" : "Mở khấn"}
                      </button>
                      <button
                        onClick={() => handleDelete(match.id)}
                        className="px-4 py-2 rounded-lg text-xs font-body cursor-pointer bg-white/[0.06] border border-white/10 text-white/40 hover:bg-red-500/20 hover:border-red-500/30 hover:text-red-400 transition"
                      >
                        Xóa
                      </button>
                    </div>
                  </div>
                );

                return (
                  <>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                      <h2 className="font-heading text-lg font-bold text-white">
                        Trận sắp tới ({upcoming.length})
                      </h2>
                    </div>
                    {upcoming.length === 0 ? (
                      <div className="glass-card rounded-2xl p-8 text-center">
                        <div className="text-white/10 text-3xl mb-2">⚔️</div>
                        <p className="text-white/30 font-body text-sm">
                          Chưa có trận sắp tới
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {upcoming.map((m) => renderRow(m, false))}
                      </div>
                    )}

                    {past.length > 0 && (
                      <div className="mt-8">
                        <div className="flex items-center gap-2 mb-4 flex-wrap">
                          <button
                            onClick={() => setShowPast((v) => !v)}
                            className="flex items-center gap-2 cursor-pointer group rounded-lg px-1 -mx-1 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/40"
                          >
                            <span className="w-1.5 h-1.5 bg-white/50 rounded-full" />
                            <h2 className="font-heading text-lg font-bold text-white/80 group-hover:text-white transition">
                              Đã qua ({past.length})
                            </h2>
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              className={`text-white/40 transition-transform ${
                                showPast ? "rotate-180" : ""
                              }`}
                            >
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </button>
                          {showPast && (
                            <button
                              onClick={() =>
                                handleDeletePast(past.map((m) => m.id))
                              }
                              className="ml-auto px-3 py-1.5 rounded-lg text-xs font-body cursor-pointer bg-white/[0.06] border border-white/10 text-white/40 hover:bg-red-500/20 hover:border-red-500/30 hover:text-red-400 transition"
                            >
                              Xóa tất cả
                            </button>
                          )}
                        </div>
                        {showPast && (
                          <div className="space-y-3 pb-8">
                            {past.map((m) => renderRow(m, true))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}

          {activeTab === "prayers" && (
            <>
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                <h2 className="font-heading text-lg font-bold text-white">
                  Danh sách lời khấn ({prayers.length}/{totalPrayerCount})
                </h2>
              </div>

              {/* Search */}
              <div className="relative mb-4">
                <input
                  type="text"
                  placeholder="Tìm theo tên hoặc nội dung lời khấn..."
                  value={prayerSearch}
                  onChange={(e) => setPrayerSearch(e.target.value)}
                  className={inputClass}
                />
                {prayerSearch && (
                  <button
                    onClick={() => setPrayerSearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white text-sm cursor-pointer bg-transparent border-none"
                  >
                    ✕
                  </button>
                )}
              </div>

              {loadingSearchPool && isSearching ? (
                <div className="glass-card rounded-2xl p-8 text-center">
                  <p className="text-white/30 font-body text-sm">Đang tải lời khấn gần đây...</p>
                </div>
              ) : filteredPrayers.length === 0 ? (
                <div className="glass-card rounded-2xl p-8 text-center">
                  <p className="text-white/30 font-body text-sm">
                    {isSearching ? `Không tìm thấy lời khấn cho "${debouncedSearch.trim()}"` : "Chưa có lời khấn nào"}
                  </p>
                </div>
              ) : (
                <div className="space-y-3 pb-8">
                  {isSearching && (
                    <p className="text-white/40 text-xs font-body">
                      Tìm thấy {filteredPrayers.length} kết quả · nội dung trong{" "}
                      {(searchPool?.length || 0).toLocaleString("vi-VN")} lời khấn gần nhất, username trong toàn bộ
                    </p>
                  )}
                  {filteredPrayers.map((prayer) => (
                    <div
                      key={prayer.id}
                      className="glass-card rounded-xl p-5 flex flex-col md:flex-row justify-between md:items-start gap-4"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <a
                            href={getThreadsUrl(prayer.threadsUsername)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:text-red-400 text-sm font-heading font-bold transition"
                          >
                            @{prayer.threadsUsername}
                          </a>
                          <span className="text-white/15">•</span>
                          <span className="text-white/30 text-xs font-body">
                            {prayer.matchLabel || "Khấn chung"}
                          </span>
                          <span className="text-white/15">•</span>
                          <span className="text-white/20 text-xs font-body">
                            {new Date(prayer.createdAt).toLocaleDateString("vi-VN", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                        <p className="text-white/70 text-sm font-body leading-relaxed">
                          {prayer.prayerContent}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeletePrayer(prayer.id)}
                        className="px-4 py-2 rounded-lg text-xs font-body cursor-pointer bg-white/[0.06] border border-white/10 text-white/40 hover:bg-red-500/20 hover:border-red-500/30 hover:text-red-400 transition shrink-0"
                      >
                        Xóa
                      </button>
                    </div>
                  ))}
                  {hasMorePrayers && !isSearching && (
                    <button
                      onClick={() => fetchPrayers(false)}
                      disabled={loadingPrayers}
                      className="w-full py-3 rounded-xl text-sm font-body cursor-pointer bg-white/[0.06] border border-white/10 text-white/40 hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition disabled:opacity-50"
                    >
                      {loadingPrayers ? "Đang tải..." : "Tải thêm"}
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {activeTab === "thanks" && (
            <>
              {/* Add Thank Message */}
              <div className="glass-card rounded-2xl p-6 md:p-8 mb-8">
                <h2 className="font-heading text-lg font-bold text-white mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                  Thêm lời cảm ơn
                </h2>
                <p className="text-white/40 text-sm font-body mb-4">
                  Lời cảm ơn sẽ hiển thị ngẫu nhiên sau khi người dùng gửi lời khấn.
                </p>
                <div className="flex gap-3">
                  <input
                    type="text"
                    placeholder="VD: Cảm ơn bạn đã cầu nguyện! T1 fighting!"
                    value={newThankMsg}
                    onChange={(e) => setNewThankMsg(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddThankMsg()}
                    className={inputClass}
                  />
                  <button
                    onClick={handleAddThankMsg}
                    disabled={!newThankMsg.trim()}
                    className="px-6 py-3.5 bg-gradient-to-r from-primary to-primary-dark hover:from-primary-light hover:to-primary text-white font-heading font-bold rounded-xl transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed shadow-[0_0_20px_rgba(220,38,38,0.2)] hover:shadow-[0_0_30px_rgba(220,38,38,0.4)] disabled:shadow-none shrink-0"
                  >
                    Thêm
                  </button>
                </div>
              </div>

              {/* Thank Messages List */}
              <div className="flex items-center gap-2 mb-4">
                <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                <h2 className="font-heading text-lg font-bold text-white">
                  Danh sách ({thankMessages.length})
                </h2>
              </div>
              {thankMessages.length === 0 ? (
                <div className="glass-card rounded-2xl p-8 text-center">
                  <p className="text-white/30 font-body text-sm">
                    Chưa có lời cảm ơn nào. Sẽ dùng mặc định: "Cảm ơn bạn đã cầu nguyện cho T1!"
                  </p>
                </div>
              ) : (
                <div className="space-y-3 pb-8">
                  {thankMessages.map((msg, i) => (
                    <div
                      key={msg.id}
                      className="glass-card rounded-xl px-5 py-3.5 flex justify-between items-center gap-4"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-white/20 text-xs font-body shrink-0">{i + 1}.</span>
                        <p className="text-white/80 text-sm font-body truncate">
                          "{msg.message}"
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeleteThankMsg(msg.id)}
                        className="px-4 py-2 rounded-lg text-xs font-body cursor-pointer bg-white/[0.06] border border-white/10 text-white/40 hover:bg-red-500/20 hover:border-red-500/30 hover:text-red-400 transition shrink-0"
                      >
                        Xóa
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;
